import { useEffect } from "react";
import { graphql, useFragment } from "react-relay";
import type { WidgetConfigItems } from "scmp-sdk/components/acquisition-popup-container/types";
import type { PopupState } from "scmp-sdk/components/contexts";
import { useSdkContext } from "scmp-sdk/components/contexts";

import type { hooksUseAcquisitionPopupQuery$key } from "scmp-app/queries/__generated__/hooksUseAcquisitionPopupQuery.graphql";

import type { AcquisitionNewsletter } from "./popups/acquisition-newsletter/types";
import type { Campaign } from "./popups/campaign/types";
import { MyNewsConfig } from "./popups/my-news/data";
import type { TopicPackageFollow } from "./popups/topic-package-follow/types";
import type { WhyRegister } from "./popups/why-register/types";

export type useAcquisitionPopupOptions = {
  configEnvironmentSlug: "dev" | "production";
};

export const useAcquisitionPopup = (
  options: useAcquisitionPopupOptions,
  queryReference: hooksUseAcquisitionPopupQuery$key,
) => {
  const {
    acquisition: { setPopupState },
  } = useSdkContext();

  const data = useFragment<hooksUseAcquisitionPopupQuery$key>(
    graphql`
      fragment hooksUseAcquisitionPopupQuery on Query {
        pptoConfig: appConfig(filter: { entityId: "scmp_pwa_ppto" }) {
          json
        }
        settings: appConfig(filter: { entityId: "scmp_pwa_settings" }) {
          json
        }
      }
    `,
    queryReference,
  );

  useEffect(() => {
    if (!data?.pptoConfig?.json || !data?.settings?.json) return;

    const widgetConfig = { ...data.pptoConfig.json, ...data.settings.json } as WidgetConfigItems;

    const popupState = Object.entries(widgetConfig).reduce<PopupState>(
      (state, [key, config]) => {
        const normalizedKey = key.toUpperCase();

        if (normalizedKey.startsWith("ACQUISITION_NL_")) {
          state.acquisitions[key] = config as AcquisitionNewsletter;
        } else if (normalizedKey.startsWith("ACQUISITION_WHY_REGISTER_")) {
          state.whyRegister[key] = config as WhyRegister;
        } else if (normalizedKey.startsWith("CAMPAIGN_POPUP_")) {
          state.campaigns[key] = config as Campaign;
        } else if (normalizedKey.startsWith("TOPIC_PACKAGE_FOLLOW_POPUP_")) {
          state.topicPackageFollows[key] = config as TopicPackageFollow;
        }

        return state;
      },
      {
        acquisitions: {},
        campaigns: {},
        myNews: {
          config: widgetConfig?.my_news,
          ...MyNewsConfig,
        },
        topicPackageFollows: {},
        whyRegister: {},
      },
    );

    setPopupState(popupState);
  }, [data?.pptoConfig?.json, data?.settings?.json, setPopupState]);
};
